// CalibrationLogBookInfo.js
// Collapsible book-info section for a Calibration Log Book: name, date, and project/phase/task.
// Edits autosave via the book-level `calibration_log_book` patch.

import React, { useMemo, useState, useEffect } from "react";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { shallow } from "zustand/shallow";
import { useAppStore } from "../../../../store/appStore";
import styles from "./CalibrationLogBookInfo.module.css";

// Parse a stored display date (MM/DD/YYYY) or ISO (YYYY-MM-DD) into a local Date.
const parseDisplayDate = (val) => {
  if (!val) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
    const [y, m, d] = val.split("-").map(Number);
    return new Date(y, m - 1, d);
  }
  const d = new Date(val);
  return isNaN(d.getTime()) ? null : d;
};

const toApiDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const toDisplayDate = (date) =>
  date.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });

export const CalibrationLogBookInfo = ({ book, emitPatch }) => {
  const projects = useAppStore((s) => s.projects.list, shallow);
  const bookInfo = useAppStore(
    (s) =>
      s.activeCalibrationLogBookActions?.calibrationLogBookFormActions?.bookInfo,
    shallow
  );

  const [expanded, setExpanded] = useState(true);
  const [bookNameLocal, setBookNameLocal] = useState(book.bookName || "");

  // Re-sync the local name field when switching to a different book.
  useEffect(() => {
    setBookNameLocal(book.bookName || "");
  }, [book.id]);

  const readOnly = !!book.completed;

  // Reconstruct selected option objects from the projects list.
  const projectOption = useMemo(
    () => (projects || []).find((p) => p.value === book.projectNumber) || null,
    [projects, book.projectNumber]
  );
  const availablePhases = projectOption?.phase_list || [];
  const phaseOption = useMemo(
    () =>
      availablePhases.find((ph) => (ph.phase_id ?? ph.value) === book.phaseId) ||
      null,
    [availablePhases, book.phaseId]
  );
  const availableTasks = phaseOption?.task_list || [];
  const taskOption = useMemo(
    () =>
      availableTasks.find((t) => (t.task_id ?? t.value) === book.taskId) || null,
    [availableTasks, book.taskId]
  );

  const handleBookNameBlur = () => {
    if (bookNameLocal !== (book.bookName || "")) {
      bookInfo?.setBookName(bookNameLocal, emitPatch);
    }
  };

  const handleDateChange = (date) => {
    if (!date) {
      bookInfo?.setBookDate(null, null, emitPatch);
      return;
    }
    bookInfo?.setBookDate(toDisplayDate(date), toApiDate(date), emitPatch);
  };

  const handleProject = (sel) =>
    bookInfo?.selectProject(sel?.value ?? null, emitPatch);
  const handlePhase = (sel) =>
    bookInfo?.selectPhase((sel?.phase_id ?? sel?.value) ?? null, emitPatch);
  const handleTask = (sel) =>
    bookInfo?.selectTask((sel?.task_id ?? sel?.value) ?? null, emitPatch);

  return (
    <div className={styles.infoSection}>
      <button
        type="button"
        className={styles.infoHeader}
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
      >
        <span>Book Information</span>
        <span className={styles.caret}>{expanded ? "▼" : "▶"}</span>
      </button>

      {expanded && (
        <div className={styles.infoBody}>
          <div className={styles.field}>
            <label className={styles.label}>Book Name</label>
            <input
              type="text"
              className={styles.input}
              value={bookNameLocal}
              onChange={(e) => setBookNameLocal(e.target.value)}
              onBlur={handleBookNameBlur}
              placeholder="Enter a book name"
              readOnly={readOnly}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Book Date</label>
            <DatePicker
              selected={parseDisplayDate(book.bookDate)}
              onChange={handleDateChange}
              dateFormat="MM/dd/yyyy"
              className={styles.input}
              placeholderText="Select date"
              showMonthDropdown
              showYearDropdown
              scrollableYearDropdown
              portalId="root"
              disabled={readOnly}
              popperProps={{
                strategy: "fixed",
                modifiers: [
                  { name: "zIndex", enabled: true, options: { zIndex: 9999 } },
                ],
              }}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Project</label>
            <Select
              value={projectOption}
              onChange={handleProject}
              options={projects || []}
              getOptionLabel={(o) => o.label}
              getOptionValue={(o) => o.value}
              placeholder="Select project"
              isClearable
              isDisabled={readOnly}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Phase</label>
            <Select
              value={phaseOption}
              onChange={handlePhase}
              options={availablePhases}
              getOptionLabel={(o) => o.label}
              getOptionValue={(o) => o.phase_id ?? o.value}
              placeholder={
                projectOption ? "Select phase" : "Select a project first"
              }
              isClearable
              isDisabled={readOnly || !projectOption}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Task</label>
            <Select
              value={taskOption}
              onChange={handleTask}
              options={availableTasks}
              getOptionLabel={(o) => o.label}
              getOptionValue={(o) => o.task_id ?? o.value}
              placeholder={
                phaseOption ? "Select task" : "Select a phase first"
              }
              isClearable
              isDisabled={readOnly || !phaseOption}
            />
          </div>
        </div>
      )}
    </div>
  );
};
